import { api } from './api';
import { AudioFeatures } from '@meradio/shared-types';

export const SET_MAX_DURATION_SECONDS = 10800;

export interface SetVideoChannel {
  id?: string;
  channelId?: string;
  name?: string;
  thumbnailUrl?: string;
}

export interface SetVideo {
  id: string;
  videoId: string;
  title: string;
  description?: string;
  thumbnailUrl?: string;
  publishedAt: string;
  duration?: number;
  channel?: SetVideoChannel;
  audioFeatures?: AudioFeatures | null;
}

export interface SetEntry {
  position: number;
  video: SetVideo;
}

export interface DjSet {
  id: string;
  name: string;
  totalDurationSeconds: number;
  entryCount: number;
  entries?: SetEntry[];
  createdAt: string;
  updatedAt: string;
}

export interface SuggestedTrack {
  video: SetVideo;
  audioFeatures?: AudioFeatures | null;
  fromCollection?: boolean;
}

export interface SetSuggestions {
  compatible: SuggestedTrack[];
  wildcards: SuggestedTrack[];
}

export const getSets = async (): Promise<DjSet[]> => {
  const response = await api.get<{ sets: DjSet[] }>('/sets');

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to load sets');
  }

  return response.data.sets;
};

export const getSet = async (setId: string): Promise<DjSet> => {
  if (!setId || setId.trim().length === 0) {
    throw new Error('Set ID is required');
  }

  const response = await api.get<{ set: DjSet }>(`/sets/${setId}`);

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to load set');
  }

  return response.data.set;
};

export const createSet = async (name: string): Promise<DjSet> => {
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    throw new Error('Set name is required');
  }

  const response = await api.post<{ set: DjSet }>('/sets', { name: trimmed });

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to create set');
  }

  return response.data.set;
};

export const renameSet = async (setId: string, name: string): Promise<DjSet> => {
  const trimmed = name.trim();
  if (!setId || trimmed.length === 0) {
    throw new Error('Set ID and name are required');
  }

  const response = await api.put<{ set: DjSet }>(`/sets/${setId}`, { name: trimmed });

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to rename set');
  }

  return response.data.set;
};

export const deleteSet = async (setId: string): Promise<void> => {
  if (!setId || setId.trim().length === 0) {
    throw new Error('Set ID is required');
  }

  const response = await api.delete(`/sets/${setId}`);

  if (!response.success) {
    throw new Error(response.error || 'Failed to delete set');
  }
};

export const addEntryToSet = async (setId: string, videoId: string): Promise<DjSet> => {
  if (!setId || !videoId) {
    throw new Error('Set ID and video ID are required');
  }

  const response = await api.post<{ set: DjSet }>(`/sets/${setId}/entries`, { videoId });

  if (!response.success || !response.data) {
    throw new Error(response.error || response.message || 'Failed to add to set');
  }

  return response.data.set;
};

export const removeEntryFromSet = async (setId: string, videoId: string): Promise<DjSet> => {
  if (!setId || !videoId) {
    throw new Error('Set ID and video ID are required');
  }

  const response = await api.delete<{ set: DjSet }>(`/sets/${setId}/entries/${videoId}`);

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to remove from set');
  }

  return response.data.set;
};

export const reorderSet = async (setId: string, orderedVideoIds: string[]): Promise<DjSet> => {
  if (!setId) {
    throw new Error('Set ID is required');
  }

  const response = await api.put<{ set: DjSet }>(`/sets/${setId}/reorder`, { videoIds: orderedVideoIds });

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to reorder set');
  }

  return response.data.set;
};

export interface GetSuggestionsParams {
  videoId: string;
  exclude?: string[];
}

export const getSetSuggestions = async ({
  videoId,
  exclude = [],
}: GetSuggestionsParams): Promise<SetSuggestions> => {
  if (!videoId || videoId.trim().length === 0) {
    throw new Error('Video ID is required');
  }

  const response = await api.get<SetSuggestions>('/sets/suggest', {
    params: { videoId, exclude: exclude.join(',') },
  });

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to load suggestions');
  }

  return response.data;
};
